import type { ExperienceEventHighlightDataInterface } from "@/libs/server/goApiServer/experience/ExperienceServiceInterface/getEventHighlightExperience";
import type { ExperienceEventUpcomingDataInterface } from "@/libs/server/goApiServer/experience/ExperienceServiceInterface/getEventUpcomingExperience";
import type { PrivilegesDataPerksDetail } from "@/libs/server/goApiServer/experience/ExperienceServiceInterface/getPrivileges";
import { parseISODuration } from "@/utils/common/dateHelper/dateConvertFormatUtills";

export type TierTypetype =
  | "general"
  | "handpick"
  | "exclusive"
  | "bestSeller"
  | "special-deal"
  | "recommended";
export interface SearchResultRequest {
  destinationId: number;
  destinationType: string;
  categorySlug: string;
}
export interface ExperienceSearchResultResponseInterface {
  status: boolean;
  experience: Experience[];
  total: number;
  totalAll: number;
  totalPage: number;
}
export interface ExperiencePerk {
  privileges: PrivilegesDataPerksDetail[];
  freebies: PrivilegesDataPerksDetail[];
}
export interface Experience {
  experienceId: string;
  name: string;
  slug: string;
  areaName: null;
  areaSlug: null;
  cityName: string;
  citySlug: string;
  countryName: string;
  countrySlug: string;
  tier: TierTypetype;
  ticketType: string;
  isCurrentMonth: boolean;
  isToday: boolean;
  isTomorrow: boolean;
  isBestSelling: boolean;
  timeDuration: null;
  isFavourite: boolean;
  isExclusive: boolean;
  klubCategory: KlubCategory;
  isGreenCertificate?: boolean;
  category: Category[];
  location: Location;
  photos: string[];
  rating: null;
  numReview: null;
  redeemStart: null;
  redeemEnd: null;
  isPrivate: boolean;
  prices: Prices;
  durationHours: number | null;
  duration: string;
  startTime: number;
  isSEO: boolean;
  weightScore: number;
  priorityTag: boolean;
  specialDeal: boolean;
  instantConfirm: boolean;
  isSoldOut: boolean;
  perks: ExperiencePerk;
}

export interface Prices {
  id: number;
  packageName: null;
  type: string;
  nameEn: string;
  nameTh: string;
  price: number;
  originalPrice: number;
  exclusivePrice: number;
  currency: string;
  settlementPrice: number;
  settlementPriceCurrency: string;
  gotherPrice: number;
  discountPercentage: number;
  gotherDiscountPercentage: number;
}

export interface Ticket {
  id: number;
  packageName: string;
  type: string;
  nameEn: string;
  nameTh: string;
  price: number;
  originalPrice: number;
  settlementPrice: string;
  currency: string;
}

export interface Location {
  lat: null;
  log: null;
}

export interface Category {
  name: string;
  slug: string;
}

export interface KlubCategory {
  name: null;
  slug: null;
}

const tierWeighting = 0.4;
const priorityWeighting = 0.3;
const marketingWeighting = 0.3;
const tierScore = {
  general: 1,
  handpick: 2,
  exclusive: 3,
  bestSeller: 0,
};

// SECTION - logic -> (0.4*tier)+(0.3*priority tag)+(0.3*marketing capsule)
const CallTier = (item: Experience) => {
  try {
    const havePrice = !!item?.prices?.price;
    const { tier, isBestSelling, isPrivate, isToday } = item;
    const isPriority = item.priorityTag;
    const isSpecialDeal = item.specialDeal;
    const isInstantConfirm = !!item?.instantConfirm;

    let tierPoint = 0;
    // NOTE - case not have price
    if (!havePrice) return 0;
    // NOTE - calculate tier
    if (isBestSelling) {
      tierPoint = tierScore.bestSeller * tierWeighting;
    } else {
      tierPoint = (tierScore[tier] || 0) * tierWeighting;
    }
    // NOTE - calculate priority tag
    const priorityPoint = isPriority ? 3 * priorityWeighting : 0;

    // NOTE - calculate marketing capsule
    const specialDeal = isSpecialDeal ? 3 : 0;
    // NOTE - green tag
    const privateGroupPoint = isPrivate ? 1 : 0;
    const bookNowPoint = isInstantConfirm ? 1 : 0;
    const greenTag = privateGroupPoint + bookNowPoint;

    // NOTE - capsule tag
    const bestSeller = isBestSelling ? 2 : 0;
    const capsule = bestSeller;

    const totalMarketingTagPoint = specialDeal + greenTag + capsule;
    const marketingCapsulePoint = totalMarketingTagPoint * marketingWeighting;

    return tierPoint + priorityPoint + marketingCapsulePoint;
  } catch {
    return 0;
  }
};

export class ExperienceSearchResultResponse {
  readonly status: boolean = false;

  readonly experience: Experience[] | null = null;

  readonly total: number = 0;

  readonly totalAll: number = 0;

  readonly totalPage: number = 0;

  constructor(data?: any) {
    this.status = data.status || false;
    const checkData: Experience[] =
      data?.result?.experiences || data?.result || [];

    const newMapData = checkData.map((item: Experience) => {
      const weightScore = CallTier(item);
      const toHours =
        item?.duration && Number.isNaN(+item.duration)
          ? parseISODuration({
              durationString: item.duration,
              returnHours: true,
            })
          : null;

      return {
        prices: item.prices,
        areaName: item.areaName || null,
        areaSlug: item.areaSlug || null,
        cityName: item.cityName || "",
        citySlug: item.citySlug || "",
        countryName: item.countryName || "",
        countrySlug: item.countrySlug || "",
        duration: item.duration || "",
        category: item.category || [],
        photos: item.photos || [],
        experienceId: item.experienceId || "",
        isBestSelling: item.isBestSelling || false,
        isCurrentMonth: item.isCurrentMonth || false,
        isExclusive: item.isExclusive || item.tier === "exclusive" || false,
        isFavourite: item.isFavourite || false,
        isPrivate: item.isPrivate || false,
        isToday: item.isToday || false,
        isTomorrow: item.isTomorrow || false,
        klubCategory: item.klubCategory || [],
        timeDuration: item.timeDuration || null,
        location: item.location || { lat: null, log: null },
        name: item.name || "",
        numReview: item.numReview || null,
        rating: item.rating || null,
        redeemEnd: item.redeemEnd || null,
        redeemStart: item.redeemStart || null,
        slug: item.slug || "",
        startTime: item.startTime || 0,
        ticketType: item.ticketType || "",
        tier: item.tier || "",
        durationHours: toHours,
        isSEO: item.isSEO || false,
        isGreenCertificate: item.isGreenCertificate || false,
        weightScore,
        priorityTag: item.priorityTag || false,
        specialDeal: item.priorityTag || false,
        instantConfirm: item.instantConfirm || false,
        isSoldOut: item.isSoldOut || false,
        perks: item.perks || {},
      };
    });
    this.experience = newMapData;
    this.total = data.total || 0;
    this.totalAll = data.totalAll;
    this.totalPage = data.totalPage;
  }
}

export type ExperienceEventDataInterface =
  | ExperienceEventHighlightDataInterface
  | ExperienceEventUpcomingDataInterface;
